import { createSlice } from '../factory';
import { getSavedUnits } from '@repo/api';
const initialState = {
    savedUnitIds: [],
    savedUnits: [],
    pendingRemovals: {},
};
export const savedUnitsSlice = createSlice({
    name: 'savedUnits',
    initialState,
    persist: {
        keys: ['savedUnitIds', 'savedUnits'], // Persist both IDs and full listing data (not pendingRemovals)
    },
    actions: ({ setSlice, getSlice }) => ({
        /**
         * Add a unit to saved units
         */
        addSavedUnit: (listing) => {
            const current = getSlice();
            const unitId = listing.id || listing.slug;
            if (!unitId)
                return;
            // Check if already saved
            if (current.savedUnitIds.includes(unitId)) {
                return;
            }
            // Remove from pendingRemovals if it was there
            const remainingRemovals = { ...current.pendingRemovals };
            delete remainingRemovals[unitId];
            setSlice({
                savedUnitIds: [...current.savedUnitIds, unitId],
                savedUnits: [...current.savedUnits, listing],
                pendingRemovals: remainingRemovals,
            });
        },
        /**
         * Remove a unit from saved units
         */
        removeSavedUnit: (unitId) => {
            const current = getSlice();
            const now = Date.now();
            setSlice({
                savedUnitIds: current.savedUnitIds.filter((id) => id !== unitId),
                savedUnits: current.savedUnits.filter((listing) => {
                    const id = listing.id || listing.slug;
                    return id !== unitId;
                }),
                pendingRemovals: {
                    ...current.pendingRemovals,
                    [unitId]: now,
                },
            });
        },
        /**
         * Set saved units from API response
         */
        setSavedUnits: (listings) => {
            const savedUnitIds = listings.map(listing => listing.id || listing.slug).filter(Boolean);
            setSlice({
                savedUnitIds,
                savedUnits: listings,
            });
        },
        /**
         * Clear all saved units
         */
        clearSavedUnits: () => {
            setSlice(initialState);
        },
        /**
         * Check if a unit is saved
         */
        isUnitSaved: (unitId) => {
            const current = getSlice();
            return current.savedUnitIds.includes(unitId);
        },
        /**
         * Fetch saved units from API and update store
         * Preserves optimistic removals by not re-adding items that were recently removed
         */
        fetchSavedUnits: async () => {
            try {
                const response = await getSavedUnits();
                const listings = response.results || [];
                const apiSavedUnitIds = listings.map(listing => listing.id || listing.slug).filter(Boolean);
                // Update the store using function updater (works better with immer)
                setSlice((prev) => {
                    const now = Date.now();
                    const REMOVAL_GRACE_PERIOD = 5000; // 5 seconds - don't re-add items removed within this window
                    // Clean up old pending removals
                    const activePendingRemovals = Object.fromEntries(Object.entries(prev.pendingRemovals || {}).filter(([, timestamp]) => now - Number(timestamp) < REMOVAL_GRACE_PERIOD));
                    // Filter out items from API that were recently removed (optimistic removals)
                    const apiIdsToAdd = apiSavedUnitIds.filter(id => {
                        const removedAt = activePendingRemovals[id];
                        if (removedAt && now - removedAt < REMOVAL_GRACE_PERIOD) {
                            // Don't re-add items that were removed within the grace period
                            return false;
                        }
                        return true;
                    });
                    // Only add items from API that aren't in current state (new saves from other devices)
                    const currentIds = new Set(prev.savedUnitIds);
                    const newIds = apiIdsToAdd.filter(id => !currentIds.has(id));
                    const newListings = listings.filter(listing => {
                        const id = listing.id || listing.slug;
                        return id && newIds.includes(id);
                    });
                    // Remove items that are in current state but not in API (unsaved from other device)
                    // But keep items that were optimistically removed (they're in pendingRemovals)
                    const removedIds = Array.from(currentIds).filter(id => !apiIdsToAdd.includes(id) && !activePendingRemovals[id]);
                    // Final state: current IDs minus removed ones, plus new ones from API
                    const finalIds = [...prev.savedUnitIds.filter(id => !removedIds.includes(id)), ...newIds];
                    // Build final listings array
                    const finalListings = [
                        ...prev.savedUnits.filter(listing => {
                            const id = listing.id || listing.slug;
                            return id && finalIds.includes(id) && !newIds.includes(id);
                        }),
                        ...newListings,
                    ];
                    return {
                        savedUnitIds: finalIds,
                        savedUnits: finalListings,
                        pendingRemovals: activePendingRemovals,
                    };
                });
            }
            catch (error) {
                console.error('[SavedUnits] Error fetching saved units:', error);
                // Don't clear existing saved units on error - keep what we have
            }
        },
    }),
});
